import { createApiMutation } from '../../../core/hooks/useMutationFactory';
import {
  setRequestStatus,
  setAuthenticated,
  setSuccess,
  setVerificationSent,
  setPhoneVerified,
  setEmailVerified,
  setEmailVerificationSent,
  setSignupComplete,
  setUserStatus,
} from '../../../core/store/slices/auth.slice';
import { ErrorSource } from '../../../core/constant/model/api/api';
import { getDataFromResponse } from '../../../core/constant/utils/react-query-utils/getDataFromResponse';
import { createApiError } from '../../../core/constant/utils/error.helper';
import {
  postMemberSignup,
  postSendVerificationCode,
  postVerifyPhone,
  postSendEmailVerificationCode,
  postCompleteSignUpVerification,
  postVerifyMFACode,
  setFullStoryIdentity,
} from '../api/account.api';
import {
  SendVerificationCodeResponse,
  SendVerificationCodeRequest,
  VerifyCodeResponse,
  VerifyCodeRequest,
  MemberSignupRequest,
  SendEmailVerificationRequest,
  CompleteSignUpRequest,
} from '../types/account';
import { normalizeUserData } from '../../../core/constant/utils/transformUserData';
import { setBasicInfo } from '../../../core/store/slices/basicInfoSlice';
import { setPersonalDetails } from '../../../core/store/slices/personalDetailsSlice';
import { setAvailability } from '../../../core/store/slices/availabilitySlice';
import { setEmergencyContact } from '../../../core/store/slices/emergencyContactSlice';
import { setPreferenceInformation } from '../../../core/store/slices/preferenceInformationSlice';
import { setMemberSubscription } from '../../../core/store/slices/memberSubscriptionSlice';
import { useNavigate } from 'react-router-dom';
import { UserStatus } from '../../../core/constant/constant';

export const useSendVerificationCodeMutation = () => {
  return createApiMutation<SendVerificationCodeResponse, SendVerificationCodeRequest>({
    mutationFn: async (request, signal) => {
      const response = await postSendVerificationCode(request, signal);
      const responseData = getDataFromResponse(response);
      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(responseData.message || 'Send verification failed', responseData);
      }

      return responseData;
    },
    errorSource: ErrorSource.SEND_CODE_VERIFICATION,
    loadingActions: {
      start: (dispatch) =>
        dispatch(
          setRequestStatus({
            isSendingCode: true,
            isLoading: true,
          })
        ),
      end: (dispatch) =>
        dispatch(
          setRequestStatus({
            isSendingCode: false,
            isLoading: false,
          })
        ),
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {
      if (responseData.memberId && variables.phoneNumber) {
        dispatch(
          setVerificationSent({
            phoneNumber: variables.phoneNumber,
            email: variables.email,
            userId: responseData.memberId,
            stage: 'phoneVerification',
          })
        );

        dispatch(
          setSuccess({
            message: 'Verification code sent successfully',
          })
        );
      }
    },
    module: 'Auth',
  });
};

export const useVerifyMFACodeMutation = () => {
  const navigate = useNavigate();
  return createApiMutation<VerifyCodeResponse, VerifyCodeRequest>({
    mutationFn: async (request, signal) => {
      const response = await postVerifyMFACode(request.userId, request.code, signal);

      const responseData = getDataFromResponse(response);
      setFullStoryIdentity(responseData?.profile);
      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(responseData.message || 'Verification failed', responseData);
      }

      return responseData;
    },
    errorSource: ErrorSource.VERIFY_CODE,
    loadingActions: {
      start: (dispatch) =>
        dispatch(
          setRequestStatus({
            isVerifyingCode: true,
            isLoading: true,
          })
        ),
      end: (dispatch) =>
        dispatch(
          setRequestStatus({
            isVerifyingCode: false,
            isLoading: false,
          })
        ),
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {
      if (responseData.accessToken) {
        const { ttl } = responseData.accessToken;
        const ttlString = typeof ttl === 'number' ? ttl.toString() : ttl;
        if (
          responseData.profile?._data.currentStatus.replace(' ', '_') === UserStatus.EXITED_BY_ADMIN
        ) {
          dispatch(
            setUserStatus(responseData.profile?._data.currentStatus.replace(' ', '_') as UserStatus)
          );
          dispatch(
            setVerificationSent({
              stage: 'initial',
              email: '',
              phoneNumber: '',
              userId: '',
            })
          );
          navigate('/auth');
          return;
        }

        // Email OTP step has been removed from the enrollment flow.
        // Authenticate directly regardless of emailVerified status.
        dispatch(
          setAuthenticated({
            token: responseData.accessToken.id,
            userId: responseData.accessToken.userId,
            ttl: ttlString,
          })
        );

        if (responseData.profile) {
          const {
            basicInfo,
            personalDetails,
            preferenceInformation,
            availability,
            emergencyContact,
            subscription,
          } = normalizeUserData(responseData.profile, responseData.subscription);

          dispatch(setBasicInfo(basicInfo));
          dispatch(setPersonalDetails(personalDetails));
          dispatch(setPreferenceInformation(preferenceInformation));
          dispatch(setAvailability(availability));
          dispatch(setEmergencyContact(emergencyContact));
          dispatch(setMemberSubscription(subscription));
        }

        dispatch(
          setSuccess({
            message: 'Successfully authenticated',
          })
        );
      }
    },
    module: 'Auth',
  });
};

export const useMemberSignupMutation = () => {
  return createApiMutation<SendVerificationCodeResponse, MemberSignupRequest>({
    mutationFn: async (request, signal) => {
      const response = await postMemberSignup(request, signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(
          responseData.message ||
            'Unable to complete registration at the moment. Please try again later',
          responseData
        );
      }

      return responseData;
    },
    errorSource: ErrorSource.MEMBER_SIGNUP,
    loadingActions: {
      start: (dispatch) =>
        dispatch(
          setRequestStatus({
            isSendingCode: true,
            isLoading: true,
          })
        ),
      end: (dispatch) =>
        dispatch(
          setRequestStatus({
            isSendingCode: false,
            isLoading: false,
          })
        ),
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {
      if (responseData.memberId && variables.phoneNumber) {
        dispatch(
          setVerificationSent({
            phoneNumber: variables.phoneNumber,
            email: variables.email,
            userId: responseData.memberId,
            stage: 'phoneVerification',
          })
        );
        const user = {
          phoneNumber: variables.phoneNumber,
          email: variables.email,
        };
        setFullStoryIdentity(user);

        dispatch(
          setSuccess({
            message: 'Verification code sent to your phone',
          })
        );
      }
    },
    module: 'Auth',
  });
};

export const useVerifyPhoneMutation = () => {
  return createApiMutation<VerifyCodeResponse, VerifyCodeRequest>({
    mutationFn: async (request, signal) => {
      const response = await postVerifyPhone(request.userId, request.code, signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(responseData.message || 'Phone verification failed', responseData);
      }

      return responseData;
    },
    errorSource: ErrorSource.VERIFY_CODE,
    loadingActions: {
      start: (dispatch) =>
        dispatch(
          setRequestStatus({
            isVerifyingCode: true,
            isLoading: true,
          })
        ),
      end: (dispatch) =>
        dispatch(
          setRequestStatus({
            isVerifyingCode: false,
            isLoading: false,
          })
        ),
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {
      // New backend: phone verify now completes signup (email OTP removed).
      // Response includes accessToken + profile, so we authenticate directly.
      if (responseData.accessToken) {
        const { ttl } = responseData.accessToken;
        const ttlString = typeof ttl === 'number' ? ttl.toString() : ttl;

        dispatch(
          setAuthenticated({
            token: responseData.accessToken.id,
            userId: responseData.accessToken.userId,
            ttl: ttlString,
          })
        );

        if (responseData.profile) {
          setFullStoryIdentity(responseData.profile);
          const {
            basicInfo,
            personalDetails,
            preferenceInformation,
            availability,
            emergencyContact,
            subscription,
          } = normalizeUserData(responseData.profile, responseData.subscription);

          dispatch(setBasicInfo(basicInfo));
          dispatch(setPersonalDetails(personalDetails));
          dispatch(setPreferenceInformation(preferenceInformation));
          dispatch(setAvailability(availability));
          dispatch(setEmergencyContact(emergencyContact));
          dispatch(setMemberSubscription(subscription));
        }

        // Jump straight to 'complete' — skips emailInitiation / emailVerification
        dispatch(setSignupComplete());

        dispatch(
          setSuccess({
            message: 'Phone verified successfully! Your account is ready.',
          })
        );
        return;
      }

      // Fallback for older backend that only returns { statusCode, message }
      dispatch(setPhoneVerified());
      dispatch(
        setSuccess({
          message: 'Phone verified successfully!',
        })
      );
    },
    module: 'Auth',
  });
};

export const useSendEmailVerificationMutation = () => {
  return createApiMutation<{ statusCode: number; message: string }, SendEmailVerificationRequest>({
    mutationFn: async (request, signal) => {
      const response = await postSendEmailVerificationCode(request.userId, request.email, signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(
          responseData.message || 'Failed to send email verification code',
          responseData
        );
      }

      return responseData;
    },
    errorSource: ErrorSource.SEND_EMAIL_VERIFICATION,
    loadingActions: {
      start: (dispatch) =>
        dispatch(
          setRequestStatus({
            isSendingCode: true,
            isLoading: true,
          })
        ),
      end: (dispatch) =>
        dispatch(
          setRequestStatus({
            isSendingCode: false,
            isLoading: false,
          })
        ),
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {
      dispatch(
        setSuccess({
          message: 'Verification code sent to your email',
        })
      );

      dispatch({
        type: 'auth/setEmailVerificationSent',
        payload: {
          email: variables.email,
          stage: 'emailVerification',
        },
      });
    },
    module: 'Auth',
  });
};

export const useCompleteSignUpVerificationMutation = () => {
  return createApiMutation<VerifyCodeResponse, CompleteSignUpRequest>({
    mutationFn: async (request, signal) => {
      const response = await postCompleteSignUpVerification(request.userId, request.code, signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(responseData.message || 'Email verification failed', responseData);
      }

      return responseData;
    },
    errorSource: ErrorSource.COMPLETE_SIGNUP,
    loadingActions: {
      start: (dispatch) =>
        dispatch(
          setRequestStatus({
            isVerifyingCode: true,
            isLoading: true,
          })
        ),
      end: (dispatch) =>
        dispatch(
          setRequestStatus({
            isVerifyingCode: false,
            isLoading: false,
          })
        ),
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {
      dispatch(setEmailVerified());

      if (responseData.accessToken) {
        const { ttl } = responseData.accessToken;
        const ttlString = typeof ttl === 'number' ? ttl.toString() : ttl;

        dispatch(
          setAuthenticated({
            token: responseData.accessToken.id,
            userId: responseData.accessToken.userId,
            ttl: ttlString,
          })
        );

        if (responseData.profile) {
          const {
            basicInfo,
            personalDetails,
            preferenceInformation,
            availability,
            emergencyContact,
            subscription,
          } = normalizeUserData(responseData.profile, responseData.subscription);

          dispatch(setBasicInfo(basicInfo));
          dispatch(setPersonalDetails(personalDetails));
          dispatch(setPreferenceInformation(preferenceInformation));
          dispatch(setAvailability(availability));
          dispatch(setEmergencyContact(emergencyContact));
          dispatch(setMemberSubscription(subscription));
        }
      }

      dispatch(
        setSuccess({
          message: 'Registration successful! Your account is now verified.',
        })
      );
    },
    module: 'Auth',
  });
};
